import React, { useEffect, useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { bankName } from '../../../shared/config/localeProfile';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, TrashIcon, EditIcon, EyeIcon, DollarSignIcon, ArrowLeftIcon, StoreIcon, CheckCircleIcon, XCircleIcon, ImageIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('id');
  const isNewShop = searchParams.get('new') === 'true';
  const isEditMode = searchParams.get('edit') === 'true' || isNewShop;
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories' | 'settings'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce'] as ('pos' | 'ecommerce')[]
  });
  // Product form state
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: 0,
    discountedPrice: undefined as number | undefined,
    categoryId: '',
    imageUrl: '',
    stockQuantity: 0,
    sku: '',
    isActive: true
  });
  // Mock data loading
  useEffect(() => {
    if (shopId && !isNewShop) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Beach Shop',
        description: 'Beachwear and accessories for summer holidays',
        logo: '•',
        color: '#f8d7d7',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 12580,
          currency: getTenant().currency.code,
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Swimwear',
        description: 'Swimsuits and beachwear',
        productCount: 12
      }, {
        id: 'cat2',
        name: 'Accessories',
        description: 'Beach accessories',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Footwear',
        description: 'Sandals and beach shoes',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Souvenirs',
        description: 'Memorable items from the beach',
        productCount: 10
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'Beach Towel',
        description: 'Large absorbent beach towel',
        price: 29.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 45,
        sku: 'BT-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Sunglasses',
        description: 'UV protection sunglasses',
        price: 49.99,
        discountedPrice: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SG-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Swim Shorts',
        description: 'Quick-dry swim shorts for men',
        price: 34.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1565992441121-4367c2967103?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
        stockQuantity: 28,
        sku: 'SS-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Beach Sandals',
        description: 'Comfortable sandals for the beach',
        price: 24.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1603487742131-4160ec999306?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'BS-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'Straw Hat',
        description: 'Wide brim straw hat for sun protection',
        price: 19.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1572307480813-ceb0e59d8325?w=300&q=80',
        stockQuantity: 15,
        sku: 'SH-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'Beach Bag',
        description: 'Spacious waterproof beach bag',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=300&q=80',
        stockQuantity: 20,
        sku: 'BB-006',
        isActive: true
      }, {
        id: 'prod7',
        name: 'Surfboard Wax',
        description: 'High quality organic surfboard wax',
        price: 9.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1531722569936-825d3dd91b15?w=300&q=80',
        stockQuantity: 100,
        sku: 'SW-007',
        isActive: true
      }, {
        id: 'prod8',
        name: 'Snorkeling Set',
        description: 'Professional snorkeling mask and fins',
        price: 59.99,
        discountedPrice: 49.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300&q=80',
        stockQuantity: 12,
        sku: 'SN-008',
        isActive: true
      }]);
    }
  }, [shopId, isNewShop]);
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  const saveShop = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNewShop) {
      navigate('/shops');
    } else {
      navigate(`/shop-details?id=${shopId}`);
    }
  };
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  const handleAddProductClick = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: 0,
      discountedPrice: undefined,
      categoryId: categories.length > 0 ? categories[0].id : '',
      imageUrl: '',
      stockQuantity: 0,
      sku: '',
      isActive: true
    });
    setShowProductForm(true);
  };
  const handleEditProductClick = (product: Product) => {
    // Navigate to dedicated edit page
    navigate(`/product-edit/${product.id}?shop=${shopId}`);
  };
  const handleDeleteProductClick = (productId: string) => {
    if (confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(p => p.id !== productId));
    }
  };
  const handleProductFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      setProducts(products.map(p => p.id === editingProduct.id ? {
        ...productForm,
        id: p.id
      } as Product : p));
    } else {
      const newProduct: Product = {
        ...productForm,
        id: `prod${Date.now()}`
      } as Product;
      setProducts([...products, newProduct]);
    }
    setShowProductForm(false);
  };
  // Render Form Mode
  if (isEditMode && !showProductForm) {
    return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-12972">
        <div className="flex items-center" data-id="element-12973">
          <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-12974">
            <ArrowLeftIcon size={20} className="mr-2" data-id="element-12975" />
            {isNewShop ? 'All shops' : 'Back to shop'}
          </button>
        </div>

        <form onSubmit={saveShop} className="space-y-6" data-id="element-12976">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-12977">
            <div className="p-8 border-b border-gray-100" data-id="element-12978">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12979">
                {isNewShop ? 'New Shop' : 'Edit Shop'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12980">
                <div data-id="element-12981">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12982">
                    {t('pages.ShopDetails.shop_name')}{' '}
                    <span className="text-red-500" data-id="element-12983">*</span>
                  </label>
                  <input type="text" required value={shopForm.name} onChange={e => setShopForm({
                  ...shopForm,
                  name: e.target.value
                })} placeholder={t('pages.ShopDetails.attrs.enter_shop_name')} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12984" />
                </div>

                <div data-id="element-12985">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12986">
                    {t('pages.ShopDetails.shop_logo')}
                  </label>
                  <input type="text" value={shopForm.logo} onChange={e => setShopForm({
                  ...shopForm,
                  logo: e.target.value
                })} placeholder={t('pages.ShopDetails.attrs.logo_character_or_emoji')} maxLength={2} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12987" />
                  <p className="mt-1.5 text-xs text-gray-500" data-id="element-12988">
                    {t('pages.ShopDetails.enter_a_single_character_or_emoji')}
                  </p>
                </div>

                <div className="md:col-span-2" data-id="element-12989">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12990">
                    {t('pages.CampaignDetails.description')}
                  </label>
                  <textarea value={shopForm.description} onChange={e => setShopForm({
                  ...shopForm,
                  description: e.target.value
                })} rows={3} placeholder={t('pages.ShopDetails.attrs.describe_your_shop')} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-12991" />
                </div>
              </div>
            </div>

            <div className="p-8" data-id="element-12992">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-12993">
                {t('pages.ShopDetails.appearance_channels')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12994">
                <div data-id="element-12995">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-12996">
                    {t('pages.ShopDetails.logo_background_color')}
                  </label>
                  <div className="flex items-center gap-3" data-id="element-12997">
                    <input type="color" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="w-10 h-10 border-0 p-0 rounded cursor-pointer" data-id="element-12998" />
                    <input type="text" value={shopForm.color} onChange={e => setShopForm({
                    ...shopForm,
                    color: e.target.value
                  })} className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors uppercase" data-id="element-12999" />
                  </div>
                </div>

                <div data-id="element-13000">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-13001">
                    {t('pages.ShopDetails.shop_channels')}
                  </label>
                  <div className="space-y-3" data-id="element-13002">
                    <label className="flex items-center cursor-pointer group" data-id="element-13003">
                      <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300 rounded transition-colors" data-id="element-13004" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-13005">
                        {t('pages.ShopDetails.ecommerce_online_shop')}
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer group" data-id="element-13006">
                      <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
                      const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
                      setShopForm({
                        ...shopForm,
                        channels: newChannels as ('pos' | 'ecommerce')[]
                      });
                    }} className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A] border-gray-300 rounded transition-colors" data-id="element-13007" />
                      <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-13008">
                        {t('pages.ShopDetails.point_of_sale_physical_store')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3" data-id="element-13009">
            <button type="button" onClick={() => navigate(isNewShop ? '/shops' : `/shop-details?id=${shopId}`)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-13010">
              {t('pages.CampaignDetails.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13011">
              {isNewShop ? 'Create shop' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>;
  }
  // Render Product Form Mode
  if (showProductForm) {
    return <div className="space-y-8 max-w-3xl mx-auto pb-12" data-id="element-13012">
        <div className="flex items-center" data-id="element-13013">
          <button onClick={() => setShowProductForm(false)} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-13014">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-13015" />
            {t('pages.ShopDetails.back_to_products')}
          </button>
        </div>

        <form onSubmit={handleProductFormSubmit} className="space-y-6" data-id="element-13016">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-13017">
            <div className="px-8 py-6 border-b border-gray-100" data-id="element-13018">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-13019">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h1>
            </div>

            <div className="p-8 space-y-10" data-id="element-13020">
              {/* Product Information */}
              <div className="space-y-6" data-id="element-13021">
                <h2 className="text-lg font-medium text-gray-900" data-id="element-13022">
                  {t('pages.ShopDetails.product_information')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13023">
                  <div data-id="element-13024">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13025">
                      {t('pages.ShopDetails.product_name')}{' '}
                      <span className="text-red-500" data-id="element-13026">*</span>
                    </label>
                    <input type="text" required value={productForm.name} onChange={e => setProductForm({
                    ...productForm,
                    name: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13027" />
                  </div>

                  <div data-id="element-13028">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13029">
                      {t('pages.ShopDetails.sku')}
                    </label>
                    <input type="text" value={productForm.sku} onChange={e => setProductForm({
                    ...productForm,
                    sku: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13030" />
                  </div>

                  <div className="md:col-span-2" data-id="element-13031">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13032">
                      {t('pages.CampaignDetails.description')}
                    </label>
                    <textarea value={productForm.description} onChange={e => setProductForm({
                    ...productForm,
                    description: e.target.value
                  })} rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13033" />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-13034">
                <div className="absolute inset-0 flex items-center" data-id="element-13035">
                  <div className="w-full border-t border-gray-200" data-id="element-13036"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-13037">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13038">
                    {t('pages.ShopDetails.pricing_inventory')}
                  </span>
                </div>
              </div>

              {/* Pricing & Inventory */}
              <div className="space-y-5" data-id="element-13039">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13040">
                  <div data-id="element-13041">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13042">
                      {t('pages.ShopDetails.price')}{' '}
                      <span className="text-red-500" data-id="element-13043">*</span>
                    </label>
                    <div className="relative" data-id="element-13044">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13045">
                        <span className="text-gray-500 sm:text-sm" data-id="element-13046">
                          {t('components.ShopDashboard.usd_2')}
                        </span>
                      </div>
                      <input type="number" required min="0" step="0.01" value={productForm.price || ''} onChange={e => setProductForm({
                      ...productForm,
                      price: parseFloat(e.target.value) || 0
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13047" />
                    </div>
                  </div>

                  <div data-id="element-13048">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13049">
                      {t('pages.ShopDetails.discounted_price')}
                    </label>
                    <div className="relative" data-id="element-13050">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13051">
                        <span className="text-gray-500 sm:text-sm" data-id="element-13052">
                          {t('components.ShopDashboard.usd_2')}
                        </span>
                      </div>
                      <input type="number" min="0" step="0.01" value={productForm.discountedPrice || ''} onChange={e => setProductForm({
                      ...productForm,
                      discountedPrice: e.target.value ? parseFloat(e.target.value) : undefined
                    })} className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13053" />
                    </div>
                  </div>

                  <div data-id="element-13054">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13055">
                      {t('pages.ShopDetails.stock_quantity')}
                    </label>
                    <input type="number" min="0" value={productForm.stockQuantity || ''} onChange={e => setProductForm({
                    ...productForm,
                    stockQuantity: parseInt(e.target.value) || 0
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13056" />
                  </div>

                  <div data-id="element-13057">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13058">
                      {t('pages.CampaignDetails.category')}
                    </label>
                    <select value={productForm.categoryId} onChange={e => setProductForm({
                    ...productForm,
                    categoryId: e.target.value
                  })} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-13059">
                      {categories.map(c => <option key={c.id} value={c.id} data-id="element-13060">
                          {c.name}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-13061">
                <div className="absolute inset-0 flex items-center" data-id="element-13062">
                  <div className="w-full border-t border-gray-200" data-id="element-13063"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-13064">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13065">
                    {t('pages.ShopDetails.product_image')}
                  </span>
                </div>
              </div>

              {/* Product Image */}
              <div className="space-y-5" data-id="element-13066">
                <div data-id="element-13067">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13068">
                    {t('pages.ShopDetails.image_url')}
                  </label>
                  <input type="text" value={productForm.imageUrl} onChange={e => setProductForm({
                  ...productForm,
                  imageUrl: e.target.value
                })} placeholder="https://images.unsplash.com/..." className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13069" />
                </div>

                <div className="mt-4" data-id="element-13070">
                  {productForm.imageUrl ? <div className="w-48 h-48 rounded-lg border border-gray-200 overflow-hidden bg-gray-50" data-id="element-13071">
                      <img src={productForm.imageUrl} alt={t('pages.ShopDetails.attrs.preview')} className="w-full h-full object-cover" data-id="element-13072" />
                    </div> : <div className="w-48 h-48 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 text-gray-400" data-id="element-13073">
                      <ImageIcon size={32} className="mb-2" data-id="element-13074" />
                      <span className="text-sm" data-id="element-13075">
                        {t('pages.ShopDetails.no_image')}
                      </span>
                    </div>}
                </div>
              </div>

              {/* Divider */}
              <div className="relative py-2" data-id="element-13076">
                <div className="absolute inset-0 flex items-center" data-id="element-13077">
                  <div className="w-full border-t border-gray-200" data-id="element-13078"></div>
                </div>
                <div className="relative flex justify-center" data-id="element-13079">
                  <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13080">
                    {t('components.CampaignList.status')}
                  </span>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-5" data-id="element-13081">
                <div data-id="element-13082">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13083">
                    {t('components.CampaignList.status')}
                  </label>
                  <select value={productForm.isActive ? 'active' : 'inactive'} onChange={e => setProductForm({
                  ...productForm,
                  isActive: e.target.value === 'active'
                })} className="w-full md:w-1/2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-13084">
                    <option value="active" data-id="element-13085">
                      {t('pages.Campaigns.active')}
                    </option>
                    <option value="inactive" data-id="element-13086">
                      {t('pages.Campaigns.inactive')}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-8 py-4 border-t border-gray-100 bg-gray-50" data-id="element-13087">
              <button type="button" onClick={() => setShowProductForm(false)} className="px-6 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-13088">
                {t('pages.CampaignDetails.cancel')}
              </button>
              <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13089">
                {editingProduct ? 'Save changes' : 'Create product'}
              </button>
            </div>
          </div>
        </form>
      </div>;
  }
  // Render Detail Mode
  if (!shop) return null;
  return <div className="max-w-[1400px] mx-auto space-y-8 pb-12" data-id="element-13090">
      <div className="flex items-center" data-id="element-13091">
        <button onClick={() => navigate('/shops')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-13092">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-13093" />
          {t('pages.ShopDetails.all_shops')}
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-13094">
        <div className="flex items-center gap-4" data-id="element-13095">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{
          backgroundColor: shop.color
        }} data-id="element-13096">
            <span className="text-2xl font-bold text-red-600" data-id="element-13097">{shop.logo}</span>
          </div>
          <div data-id="element-13098">
            <div className="flex items-center gap-3" data-id="element-13099">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-13100">
                {shop.name}
              </h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${shop.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-13101">
                {shop.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5" data-id="element-13102">
              {shop.channels.includes('pos') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-13103">
                  {t('pages.ShopDetails.pos')}
                </span>}
              {shop.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-13104">
                  {t('pages.ShopDetails.ecommerce')}
                </span>}
              <span className="text-sm text-gray-500 ml-1" data-id="element-13105">
                {shop.description}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-13106">
          <button onClick={() => navigate(`/shop-details?id=${shop.id}&edit=true`)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.ShopDetails.attrs.edit_shop')} data-id="element-13107">
            <EditIcon size={18} data-id="element-13108" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.Sales.qr_code')} data-id="element-13109">
            <QrCodeIcon size={18} data-id="element-13110" />
          </button>
          <button onClick={() => window.open(`https://shop.${bankName().toLowerCase().replace(/\s+/g, '')}.com.my/`, '_blank', 'noopener,noreferrer')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200" title={t('pages.ShopDetails.attrs.shop_link')} data-id="element-13111">
            <LinkIcon size={18} data-id="element-13112" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-13113">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13114">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13115">
            <PackageIcon size={16} className="text-gray-500" data-id="element-13116" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13117">
              {t('pages.ShopDetails.products')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13118">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13119">
              {shop.productsCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13120">
            {t('pages.ShopDetails.active_items_in_catalog')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13121">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13122">
            <DollarSignIcon size={16} className="text-gray-500" data-id="element-13123" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13124">
              {t('components.ShopDashboard.revenue')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13125">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13126">
              {t('components.ShopDashboard.usd_2')}{' '}
              {shop.revenue.total.toLocaleString()}
            </div>
          </div>
          <div className={`px-4 py-2 text-xs font-medium ${shop.revenue.growth >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-13127">
            {shop.revenue.growth >= 0 ? '+' : ''}
            {shop.revenue.growth}
            {t('pages.ShopDetails.from_last_month')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13128">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13129">
            <ShoppingBagIcon size={16} className="text-gray-500" data-id="element-13130" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13131">
              {t('pages.Orders.orders')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1" data-id="element-13132">
            <div className="text-2xl font-bold text-gray-900" data-id="element-13133">
              {shop.ordersCount}
            </div>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13134">
            {t('pages.ShopDetails.total_completed_orders')}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col" data-id="element-13135">
          <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2" data-id="element-13136">
            <StoreIcon size={16} className="text-gray-500" data-id="element-13137" />
            <span className="text-sm font-semibold text-gray-900" data-id="element-13138">
              {t('components.CampaignList.status')}
            </span>
          </div>
          <div className="px-4 py-4 flex-1 flex items-center" data-id="element-13139">
            {shop.isOpen ? <div className="flex items-center text-green-600" data-id="element-13140">
                <CheckCircleIcon size={24} className="mr-2" data-id="element-13141" />
                <span className="text-xl font-bold" data-id="element-13142">
                  {t('pages.ShopDetails.accepting_orders')}
                </span>
              </div> : <div className="flex items-center text-red-600" data-id="element-13143">
                <XCircleIcon size={24} className="mr-2" data-id="element-13144" />
                <span className="text-xl font-bold" data-id="element-13145">
                  {t('pages.ShopDetails.currently_closed')}
                </span>
              </div>}
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium" data-id="element-13146">
            {t('pages.ShopDetails.toggle_in_edit_mode')}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-13147">
        <div className="flex space-x-8" role="tablist" aria-label="Shop sections" data-id="element-13148">
          {[{
          id: 'dashboard',
          label: 'Dashboard'
        }, {
          id: 'products',
          label: 'Products'
        }, {
          id: 'categories',
          label: 'Categories'
        }, {
          id: 'messages',
          label: 'Messages'
        }, {
          id: 'settings',
          label: 'Settings'
        }].map(tab => <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} aria-controls={`${tab.id}-tabpanel`} id={`shop-tab-${tab.id}`} onClick={() => tab.id === 'settings' ? navigate(`/shop-settings/${shopId || 'new'}`) : tab.id === 'messages' ? navigate(`/shop-settings/${shopId || 'new'}?section=messages`) : setActiveTab(tab.id as 'dashboard' | 'products' | 'categories' | 'settings')} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#E2231A] text-[#9F140F]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-13149">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      <div className="pt-2" data-id="element-13150">
        {activeTab === 'dashboard' && <ShopDashboard shop={shop} data-id="element-13151" />}

        {activeTab === 'products' && <div className="space-y-6" data-id="element-13152">
            {/* Products Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-13153">
              <div className="flex items-center gap-3" data-id="element-13154">
                <div className="relative w-full sm:w-64" data-id="element-13155">
                  <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-13156" />

                  <input type="search" placeholder={t('pages.ShopDetails.attrs.search_products')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13157" />
                </div>

                <div className="relative" data-id="element-13158">
                  <FilterIcon size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-13159" />

                  <select value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors appearance-none cursor-pointer" data-id="element-13160">
                    <option value="all" data-id="element-13161">
                      {t('pages.ShopDetails.all_categories')}
                    </option>
                    {categories.map(c => <option key={c.id} value={c.id} data-id="element-13162">
                        {c.name}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3" data-id="element-13163">
                <div className="flex items-center bg-gray-100 rounded-lg p-1" data-id="element-13164">
                  <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title={t('pages.ShopDetails.attrs.grid_view')} data-id="element-13165">
                    <LayoutGridIcon size={16} data-id="element-13166" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title={t('pages.ShopDetails.attrs.list_view')} data-id="element-13167">
                    <ListIcon size={16} data-id="element-13168" />
                  </button>
                </div>

                <button onClick={handleAddProductClick} className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13169">
                  <PlusIcon size={16} data-id="element-13170" />
                  {t('pages.ShopDetails.add_product')}
                </button>
              </div>
            </div>

            {/* Products Grid/List */}
            {filteredProducts.length === 0 ? <div className="text-center py-16 bg-white rounded-xl border border-gray-200" data-id="element-13171">
                <PackageIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-13172" />

                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-13173">
                  {t('pages.ShopDetails.no_products_found')}
                </h3>
                <p className="text-gray-500 mb-6" data-id="element-13174">
                  {t('pages.ShopDetails.try_adjusting_your_search_or')}
                </p>
                <button onClick={handleAddProductClick} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-13175">
                  <PlusIcon size={16} data-id="element-13176" />
                  {t('pages.ShopDetails.add_new_product')}
                </button>
              </div> : viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-13177">
                {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group" data-id="element-13178">
                    <div className="aspect-square relative overflow-hidden bg-gray-100" data-id="element-13179">
                      <img src={product.imageUrl} alt={product.name} onError={e => {
                ;
                (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
              }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer" onClick={() => navigate(`/product-detail/${product.id}?shop=${shopId}`)} data-id="element-13180" />

                      {!product.isActive && <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center" data-id="element-13181">
                          <span className="px-3 py-1 bg-gray-900 text-white text-xs font-medium rounded-full" data-id="element-13182">
                            {t('pages.Campaigns.inactive')}
                          </span>
                        </div>}
                      {product.discountedPrice && product.isActive && <div className="absolute top-3 right-3" data-id="element-13183">
                          <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded" data-id="element-13184">
                            {t('pages.ShopDetails.sale', 'Sale')}
                          </span>
                        </div>}
                    </div>
                    <div className="p-4" data-id="element-13185">
                      <div className="flex justify-between items-start mb-1" data-id="element-13186">
                        <h3 className="font-medium text-gray-900 truncate pr-2" data-id="element-13187">
                          {product.name}
                        </h3>
                        <div className="flex items-center gap-1" data-id="element-13188">
                          <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-[#9F140F] hover:bg-[#FEE7E5] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_product')} data-id="element-13189">
                            <EditIcon size={14} data-id="element-13190" />
                          </button>
                          <button onClick={() => toggleProductStatus(product.id)} className={`p-1.5 rounded transition-colors ${product.isActive ? 'text-gray-400 hover:text-red-600 hover:bg-red-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`} title={product.isActive ? 'Deactivate product' : 'Activate product'} data-id="element-13191">
                            {product.isActive ? <EyeIcon size={14} data-id="element-13192" /> : <EyeIcon size={14} className="opacity-50" data-id="element-13193" />}
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mb-3" data-id="element-13194">
                        {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
                      </div>
                      <div className="flex items-center justify-between mt-auto" data-id="element-13195">
                        <div className="flex items-center gap-2" data-id="element-13196">
                          {product.discountedPrice ? <>
                              <span className="font-bold text-gray-900" data-id="element-13197">
                                {formatCurrencyAmount(product.discountedPrice, {
                        fractionDigits: 2
                      })}
                              </span>
                              <span className="text-xs text-gray-400 line-through" data-id="element-13198">
                                {formatCurrencyAmount(product.price, {
                        fractionDigits: 2
                      })}
                              </span>
                            </> : <span className="font-bold text-gray-900" data-id="element-13199">
                              {formatCurrencyAmount(product.price, {
                      fractionDigits: 2
                    })}
                            </span>}
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-13200">
                          {product.stockQuantity}{' '}
                          {t('pages.ShopDetails.in_stock', 'in stock')}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13201">
                <table className="w-full text-left border-collapse" data-id="element-13202">
                  <thead data-id="element-13203">
                    <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-13204">
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13205">
                        {t('pages.ShopDetails.product')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13206">
                        {t('pages.CampaignDetails.category')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13207">
                        {t('pages.ShopDetails.price')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13208">
                        {t('pages.ShopDetails.inventory')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13209">
                        {t('components.CampaignList.status')}
                      </th>
                      <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13210">
                        {t('pages.CampaignDetails.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-13211">
                    {filteredProducts.map(product => <tr key={product.id} className={`hover:bg-gray-50 transition-colors ${!product.isActive ? 'opacity-60' : ''}`} data-id="element-13212">
                        <td className="px-6 py-4" data-id="element-13213">
                          <div className="flex items-center gap-4" data-id="element-13214">
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-13215">
                              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" data-id="element-13216" />
                            </div>
                            <div data-id="element-13217">
                              <div className="font-medium text-gray-900" data-id="element-13218">
                                {product.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5" data-id="element-13219">
                                {t('pages.ShopDetails.sku')} {product.sku}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-13220">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700" data-id="element-13221">
                            {categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized'}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-13222">
                          {product.discountedPrice ? <div data-id="element-13223">
                              <div className="font-medium text-gray-900" data-id="element-13224">
                                {formatCurrencyAmount(product.discountedPrice, {
                        fractionDigits: 2
                      })}
                              </div>
                              <div className="text-xs text-gray-400 line-through mt-0.5" data-id="element-13225">
                                {formatCurrencyAmount(product.price, {
                        fractionDigits: 2
                      })}
                              </div>
                            </div> : <div className="font-medium text-gray-900" data-id="element-13226">
                              {formatCurrencyAmount(product.price, {
                      fractionDigits: 2
                    })}
                            </div>}
                        </td>
                        <td className="px-6 py-4" data-id="element-13227">
                          <div className="text-sm text-gray-900" data-id="element-13228">
                            {product.stockQuantity}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-13229">
                          {product.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800" data-id="element-13230">
                              {t('pages.Campaigns.active')}
                            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600" data-id="element-13231">
                              {t('pages.Campaigns.inactive')}
                            </span>}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-13232">
                          <div className="flex items-center justify-end gap-2" data-id="element-13233">
                            <button onClick={() => toggleProductStatus(product.id)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" title={product.isActive ? 'Deactivate' : 'Activate'} data-id="element-13234">
                              {product.isActive ? <EyeIcon size={16} data-id="element-13235" /> : <EyeIcon size={16} className="opacity-50" data-id="element-13236" />}
                            </button>
                            <button onClick={() => handleEditProductClick(product)} className="p-1.5 text-gray-400 hover:text-[#9F140F] hover:bg-[#FEE7E5] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_product')} data-id="element-13237">
                              <EditIcon size={16} data-id="element-13238" />
                            </button>
                            <button onClick={() => handleDeleteProductClick(product.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('pages.ShopDetails.attrs.delete_product')} data-id="element-13239">
                              <TrashIcon size={16} data-id="element-13240" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {activeTab === 'categories' && <div className="space-y-6" data-id="element-13241">
            <div className="flex justify-between items-center" data-id="element-13242">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-13243">
                {t('pages.ShopDetails.product_categories')}
              </h2>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13244">
                <PlusIcon size={16} data-id="element-13245" />
                {t('pages.ShopDetails.add_category')}
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-13246">
              <table className="w-full text-left border-collapse" data-id="element-13247">
                <thead data-id="element-13248">
                  <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-13249">
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13250">
                      {t('pages.ShopDetails.category_name')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13251">
                      {t('pages.CampaignDetails.description')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13252">
                      {t('pages.ShopDetails.products')}
                    </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-13253">
                      {t('pages.CampaignDetails.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-13254">
                  {categories.map(category => <tr key={category.id} className="hover:bg-gray-50 transition-colors" data-id="element-13255">
                      <td className="px-6 py-4" data-id="element-13256">
                        <div className="font-medium text-gray-900" data-id="element-13257">
                          {category.name}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-13258">
                        <div className="text-sm text-gray-500" data-id="element-13259">
                          {category.description}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-13260">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700" data-id="element-13261">
                          {category.productCount}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-13262">
                        <div className="flex items-center justify-end gap-2" data-id="element-13263">
                          <button className="p-1.5 text-gray-400 hover:text-[#9F140F] hover:bg-[#FEE7E5] rounded transition-colors" title={t('pages.ShopDetails.attrs.edit_category')} data-id="element-13264">
                            <EditIcon size={16} data-id="element-13265" />
                          </button>
                          <button className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title={t('pages.ShopDetails.attrs.delete_category')} data-id="element-13266">
                            <TrashIcon size={16} data-id="element-13267" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}